import { action, computed, makeObservable } from 'mobx';
import { matchPath } from 'react-router-dom';
import urlParse from 'url-parse';
import { RouterStore } from '../../stores/reactRouterStore/RouterStore';
import { PublicRoute, AppRoutes } from '../routes/AppRoutes';
import { ErrorReporter } from '../../errorHandling/ErrorReporter';

export type AppPage = 'home' | 'category' | 'subCategory' | 'searchResults';

export class RoutingStore extends RouterStore {
  constructor(public routes: AppRoutes, private errorReporter: ErrorReporter) {
    super();

    makeObservable<RoutingStore, 'matches' | 'isSubCategoryPage' | 'isRootPage'>(this, {
      matches: computed,
      appPage: computed,
      categorySlug: computed,
      subCategorySlug: computed,
      alternateCanonicalLink: computed,
      canonicalLink: computed,
      page: computed,
      isCategoryPage: computed,
      isSubCategoryPage: computed,
      isRootPage: computed,
      isSearchResultsPage: computed,
      searchResultsPage: computed,
      searchQuery: computed,
      search: action,
    });
  }

  private get matches(): any {
    return Object.keys(this.routes).reduce((res, key) => {
      let path;
      let route: PublicRoute;

      try {
        route = this.routes[key];
        path = typeof route === 'object' && route.pattern && route.pattern();
      } catch (error) {
        this.errorReporter.reportError(error, { message: `Failed trying to find ${key} in ${this.routes}` });
      }

      return {
        ...res,
        ...(path && {
          [key]: matchPath(this.location.pathname.toLowerCase(), { path }),
        }),
      };
    }, {});
  }

  get appPage(): AppPage | undefined {
    if (this.isRootPage) {
      return 'home';
    }
    if (this.isCategoryPage) {
      return 'category';
    }
    if (this.isSubCategoryPage) {
      return 'subCategory';
    }
    if (this.isSearchResultsPage) {
      return 'searchResults';
    }

    return undefined;
  }

  get categorySlug(): string {
    const match = this.matches.categoryRoute;
    return match && match.params && match.params.categorySlug;
  }

  get subCategorySlug(): string {
    const match = this.matches.subCategoryRoute;
    if (!this.isSubCategoryPage) {
      return undefined;
    }
    return match && match.params && match.params.subCategorySlug;
  }

  getRedirectTarget(totalPages?: number): string | null {
    if (!this.isValidPage(totalPages)) {
      if (this.isCategoryPage) {
        return this.routes.categoryRoute.get(this.categorySlug);
      }
      if (this.isSubCategoryPage) {
        return this.routes.subCategoryRoute.get(this.categorySlug, this.subCategorySlug);
      }
    }

    if (this.isCategoryPage && this.routes.isPrimaryCategoryRoute(this.categorySlug, this.page)) {
      return this.routes.base.get();
    }

    return null;
  }

  private getCanonicalLink(page?: number): string {
    if (this.appPage === 'category') {
      return this.routes.categoryRoute.get(this.categorySlug, page);
    }
    if (this.appPage === 'subCategory') {
      return this.routes.subCategoryRoute.get(this.categorySlug, this.subCategorySlug, page);
    }
    if (this.appPage === 'home') {
      return this.routes.base.get();
    }
  }

  get alternateCanonicalLink(): string {
    return this.getCanonicalLink();
  }

  get canonicalLink(): string {
    return this.getCanonicalLink(this.page);
  }

  get page(): number | undefined {
    if (this.isSearchResultsPage) {
      return this.searchResultsPage;
    }
    const categoryMatch = this.isCategoryPage && this.matches.categoryRoute;
    const subCategoryMatch = this.isSubCategoryPage && this.matches.subCategoryRoute;

    const match = categoryMatch || subCategoryMatch;
    return match?.params?.page && parseInt(match.params.page, 10);
  }

  private isValidPage(totalPages?: number): boolean {
    if (this.page === undefined) {
      return true;
    }

    return this.page > 1 && (this.page <= totalPages || totalPages === undefined);
  }

  public get isCategoryPage(): boolean {
    const match = this.matches.categoryRoute;
    return match && match.isExact;
  }

  private get isSubCategoryPage(): boolean {
    const match = this.matches.subCategoryRoute;
    return match && match.isExact && !this.isCategoryPage;
  }

  private get isRootPage(): boolean {
    return !this.isCategoryPage && !this.isSearchResultsPage && !this.isSubCategoryPage;
  }

  public get isSearchResultsPage(): boolean {
    const match = this.matches.base;
    const parsed = urlParse(this.location.search, true);
    return match && !!parsed.query.criteria;
  }

  get searchResultsPage(): number {
    const match = this.matches.base;
    const parsed = urlParse(this.location.search, true);
    return (match && parsed && parsed.query && parsed.query.page && parseInt(parsed.query.page, 10)) || 1;
  }

  get searchQuery(): string {
    const match = this.matches.base;
    const parsed = urlParse(this.location.search, true);
    return match && parsed.query.criteria;
  }

  search(query: string, page?: number) {
    const url = query ? this.routes.searchRoute.get(query, page) : this.routes.categoryRoute.get('most-popular');
    this.history.push(url);
  }
}
